import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { usePlanetStore } from '../store/planetStore';
import { usePlayerStore } from '../store/playerStore';
import { useUniverseStore } from '../store/universeStore';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { useNow } from '../hooks/useNow';
import { useHoldRepeat } from '../hooks/useHoldRepeat';
import { usePlanetResource } from '../hooks/usePlanetResource';
import QuantityBar from '../components/QuantityBar';
import { startRecycle, cancelRecycle, rushRecycle, deliverRecycle, type PlanetActionOptions } from '../services/planet.service';
import { getShipIcon } from '../utils/shipIcons';
import { formatCountdown, formatDuration } from '../utils/formatters';
import { Button } from '../components/ui/Button';
import { Panel } from '../components/ui/Panel';
import PageLoadingState from '../components/PageLoadingState';
import DarkMatterAmount from '../components/DarkMatterAmount';
import ResourceQuantities from '../components/ResourceQuantities';
import { linkButtonClasses } from '../components/ui/buttonStyles';
import {
    BUILDING_NAMES,
    RESOURCE_NAMES,
    SCRAP_RECYCLE_DAILY_LIMIT,
    SCRAP_RECYCLE_REFUND_MAX,
    SCRAP_RECYCLE_REFUND_MIN,
    SHIP_DEFINITIONS,
    getShipDefinition,
    type PlayerRace,
    type ShipId,
    type TickCheckpointEventOf,
} from '@kaeliux/shared';
import {
    calculateDockedPopulation,
    calculateFreePopulation,
    calculateRecyclingIncomePercent,
    calculateRushCost,
    calculateShipBuildTime,
    extractBuildingLevels,
    extractScienceLevels,
    getUnmetRequirements,
    recycleScrapRequired,
} from '@kaeliux/game-engine';

const REFUND_RANGE_LABEL = `${SCRAP_RECYCLE_REFUND_MIN * 100}–${SCRAP_RECYCLE_REFUND_MAX * 100}%`;

function summarizeSelection(ships: Partial<Record<ShipId, number>>, race: PlayerRace): string {
    return (Object.entries(ships) as [string, number | undefined][])
        .filter((entry): entry is [string, number] => !!entry[1])
        .map(([id, qty]) => `${qty} × ${getShipDefinition(race, Number(id) as ShipId).name}`)
        .join(', ');
}

/**
 * When a batch actually docks. Batches queued before the 48h hold shipped
 * have no `deliverAt` and resolved at `endDate` — mirrors the engine's
 * `checkpointTime`, and keeps those rows from throwing on `undefined`.
 */
function deliverAtMs(event: TickCheckpointEventOf<'scrapRecycle'>): number {
    return (event.eventDetails.deliverAt ?? event.endDate).getTime();
}

interface QueuedRecycleRowProps {
    planetId: string;
    race: PlayerRace;
    event: TickCheckpointEventOf<'scrapRecycle'>;
    now: number;
    options: PlanetActionOptions;
}

/**
 * One in-progress recycle batch. Unlike a shipBuild/buildingBuild queue
 * entry (always exactly one item, which `QueueItemCard` is built for), a
 * recycle batch can span MULTIPLE ship types at once — so this renders one
 * icon+count per ship type instead of collapsing them into a single
 * misleading combined total.
 *
 * Delivery is held automatically for AUTO_DELIVERY_HOLD_HOURS after
 * processing finishes (`endDate`) — see `deliverAt` — but can be requested
 * on demand any time from `endDate` onward; once ready, the batch can no
 * longer be cancelled (see `cancelScrapRecycleEvent`), same as a Robots
 * ship-build group.
 */
function QueuedRecycleRow({ planetId, race, event, now, options }: QueuedRecycleRowProps) {
    const { openDialog } = useConfirmDialog();
    const [isCancelling, setIsCancelling] = useState(false);
    const [isDelivering, setIsDelivering] = useState(false);
    const [isRushing, setIsRushing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const darkMatter = usePlayerStore((s) => s.premium?.darkMatter ?? 0);

    const shipEntries = (Object.entries(event.eventDetails.ships) as [string, number | undefined][])
        .filter((entry): entry is [string, number] => !!entry[1])
        .map(([id, quantity]) => ({ def: getShipDefinition(race, Number(id) as ShipId), quantity }));
    const awaitingDelivery = now >= event.endDate.getTime();
    const remainingMs = awaitingDelivery ? deliverAtMs(event) - now : event.endDate.getTime() - now;
    // Rushing only skips PROCESSING time — once awaitingDelivery, processing
    // is already done and there's nothing left to rush (deliverRecycle skips
    // the delivery hold instead).
    const rushCost = calculateRushCost((event.endDate.getTime() - now) / 1_000);

    async function doCancel() {
        setIsCancelling(true);
        setError(null);
        try {
            const updated = await cancelRecycle(planetId, event.eventId, options);
            usePlanetStore.setState({ currentPlanet: updated });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Cancel failed');
        } finally {
            setIsCancelling(false);
        }
    }

    async function doRush() {
        setIsRushing(true);
        setError(null);
        try {
            const { planet: updated, premium } = await rushRecycle(planetId, event.eventId, options);
            usePlanetStore.setState({ currentPlanet: updated });
            usePlayerStore.setState({ premium });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Rush failed');
        } finally {
            setIsRushing(false);
        }
    }

    function requestCancel() {
        if (awaitingDelivery) return;
        openDialog({
            title: 'Cancel this recycle batch?',
            message: `You'll be refunded ${REFUND_RANGE_LABEL} of the ${RESOURCE_NAMES.scrap} this batch consumed.`,
            confirmLabel: 'Cancel batch',
            danger: true,
            onConfirm: () => { doCancel().catch(() => { }); },
        });
    }

    function requestRush() {
        openDialog({
            title: 'Rush this recycle batch?',
            message: `Instantly finish processing for ${rushCost} Dark Matter. This is non-refundable.`,
            confirmLabel: 'Rush',
            onConfirm: () => { doRush().catch(() => { }); },
        });
    }

    async function doDeliver() {
        setIsDelivering(true);
        setError(null);
        try {
            const updated = await deliverRecycle(planetId, event.eventId, options);
            usePlanetStore.setState({ currentPlanet: updated });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Delivery failed');
        } finally {
            setIsDelivering(false);
        }
    }

    return (
        <div className="flex flex-col gap-2 rounded-control border border-status-info/40 bg-status-info/10 hover:border-status-info transition-colors px-3 py-2 w-fit max-w-full shrink-0">
            <div className="flex flex-wrap gap-3">
                {shipEntries.map(({ def, quantity }) => (
                    <div key={def.id} className="flex flex-col items-center gap-1 w-16" title={`${quantity} × ${def.name}`}>
                        <div className="relative w-10 h-10 rounded-control border border-hairline bg-surface-void/60 flex items-center justify-center overflow-hidden shrink-0">
                            <img src={getShipIcon(race, def.id)} alt="" className="w-full h-full object-contain" />
                        </div>
                        <span className="text-status-info text-micro font-semibold text-center leading-tight truncate w-full">{def.name}</span>
                        <span className="text-status-info text-label font-bold">× {quantity.toLocaleString()}</span>
                    </div>
                ))}
            </div>
            <span className="text-content-muted text-micro">
                {awaitingDelivery
                    ? `Ready — docks automatically in ${formatCountdown(remainingMs)} if not delivered`
                    : `Recycling… ${formatCountdown(remainingMs)}`}
            </span>
            {error && <span className="text-status-danger text-micro" role="alert">{error}</span>}
            <div className="flex gap-2 self-start">
                {awaitingDelivery && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => { doDeliver().catch(() => { }); }}
                        disabled={isDelivering}
                    >
                        {isDelivering ? 'Delivering…' : 'Deliver Now'}
                    </Button>
                )}
                {!awaitingDelivery && (
                    <Button
                        variant="accent"
                        size="sm"
                        onClick={requestRush}
                        disabled={isRushing || darkMatter < rushCost}
                        title={`Instantly finish processing for ${rushCost} Dark Matter`}
                        aria-label={`Rush for ${rushCost} Dark Matter`}
                    >
                        {isRushing ? 'Rushing…' : <span>Rush (<DarkMatterAmount amount={rushCost} />)</span>}
                    </Button>
                )}
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={requestCancel}
                    disabled={isCancelling || awaitingDelivery}
                >
                    {isCancelling ? 'Cancelling…' : 'Cancel'}
                </Button>
            </div>
        </div>
    );
}

interface RecycleShipRowProps {
    def: ReturnType<typeof getShipDefinition>;
    race: PlayerRace;
    plantLevel: number;
    quantity: number;
    scrapAvailable: number;
    otherCommittedScrap: number;
    /** Free planet population left for a new recycle batch, before this row's own selection. */
    populationAvailable: number;
    /** Population already committed by OTHER selected ship types in this batch. */
    otherCommittedPopulation: number;
    /** No free recycling slot for a new batch — a selection can't be submitted regardless of scrap, so growing it further is disabled too. */
    hasFreeSlot: boolean;
    onChange: (delta: number) => void;
    /** Absolute set, used by clicking directly on the bar (vs. the relative +/- steppers). */
    onSetQuantity: (quantity: number) => void;
}

/** One selectable ship type — a bar with −/+ ends, filled by this ship's share of the scrap budget. Clicking the bar itself jumps to that position; the ends support press-and-hold to auto-repeat. */
function RecycleShipRow({ def, race, plantLevel, quantity, scrapAvailable, otherCommittedScrap, populationAvailable, otherCommittedPopulation, hasFreeSlot, onChange, onSetQuantity }: RecycleShipRowProps) {
    const costForQuantity = (qty: number) => recycleScrapRequired(race, { [def.id]: qty }, plantLevel);
    const unitCost = costForQuantity(1);
    const thisCost = quantity > 0 ? costForQuantity(quantity) : 0;
    const marginalNextCost = costForQuantity(quantity + 1) - costForQuantity(quantity);
    const thisPopulation = quantity * def.hangar;
    const canIncrementScrap = otherCommittedScrap + thisCost + marginalNextCost <= scrapAvailable;
    const canIncrementPopulation = otherCommittedPopulation + thisPopulation + def.hangar <= populationAvailable;
    const canIncrement = hasFreeSlot && canIncrementScrap && canIncrementPopulation;
    const canDecrement = quantity > 0;
    const pct = scrapAvailable > 0 ? Math.min(100, (thisCost / scrapAvailable) * 100) : 0;

    // The bar's full width represents `scrapAvailable`, but two separate
    // ceilings can sit below it: scrap already committed to OTHER ships in
    // this selection, and the planet's free population (issue #119).
    // `maxQuantity` is whichever binds first, and `budgetFraction` marks it
    // on the bar — in scrap terms, since that's what the width measures.
    const scrapBudget = Math.max(0, scrapAvailable - otherCommittedScrap);
    const populationBudget = Math.max(0, populationAvailable - otherCommittedPopulation);
    const maxQuantityByScrap = unitCost > 0 ? Math.floor(scrapBudget / unitCost) : 0;
    const maxQuantityByPopulation = def.hangar > 0 ? Math.floor(populationBudget / def.hangar) : Number.POSITIVE_INFINITY;
    const maxQuantity = Math.min(maxQuantityByScrap, maxQuantityByPopulation);
    const populationBinds = maxQuantityByPopulation < maxQuantityByScrap;
    const scrapBudgetFraction = scrapAvailable > 0 ? Math.min(1, scrapBudget / scrapAvailable) : 0;
    const budgetFraction = populationBinds && scrapAvailable > 0
        ? Math.min(scrapBudgetFraction, costForQuantity(maxQuantity) / scrapAvailable)
        : scrapBudgetFraction;

    const canIncrementRef = useRef(canIncrement);
    canIncrementRef.current = canIncrement;
    const canDecrementRef = useRef(canDecrement);
    canDecrementRef.current = canDecrement;

    const decrementHold = useHoldRepeat(useCallback(() => onChange(-1), [onChange]), canDecrementRef);
    const incrementHold = useHoldRepeat(useCallback(() => onChange(1), [onChange]), canIncrementRef);

    function setQuantityFromFraction(fraction: number) {
        if (unitCost <= 0 || scrapAvailable <= 0) return;
        // Clicking at or past the budget marker fills to the max this ship
        // can actually take, rather than under-filling proportionally to
        // a budget the click position was never measured against.
        if (fraction >= budgetFraction) {
            onSetQuantity(maxQuantity);
            return;
        }
        const targetQuantity = Math.floor((fraction * scrapAvailable) / unitCost);
        onSetQuantity(Math.min(maxQuantity, targetQuantity));
    }

    // The bar's arrow keys mirror the flanking −/+ buttons, so they honour
    // the same disabled state those buttons render — otherwise ArrowRight
    // walks straight past the scrap and population ceilings.
    function stepQuantity(delta: number) {
        if (delta > 0 ? !canIncrement : !canDecrement) return;
        onChange(delta);
    }

    return (
        <Panel variant="solid" padding="sm" className="flex items-center gap-3" data-testid="recycle-ship-card">
            <img
                src={getShipIcon(race, def.id)}
                alt=""
                className="w-14 h-14 rounded-control border border-hairline bg-surface-void/60 object-contain shrink-0"
            />
            <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                    <h3 className="text-content-primary font-semibold truncate">{def.name}</h3>
                    <span className="text-content-muted text-label shrink-0">
                        <ResourceQuantities amounts={{ scrap: costForQuantity(1) }} includeZero /> / unit
                    </span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        className="w-8 h-8 !px-0"
                        onClick={() => onChange(-1)}
                        {...decrementHold}
                        disabled={!canDecrement}
                        aria-label={`Decrease ${def.name} quantity`}
                    >
                        −
                    </Button>
                    <QuantityBar
                        label={`${def.name} quantity`}
                        value={quantity}
                        pct={pct}
                        fillClassName="bg-status-success/60"
                        disabled={!hasFreeSlot}
                        onSetFraction={setQuantityFromFraction}
                        onStep={stepQuantity}
                    >
                        {quantity === 0 && budgetFraction < 1 && (
                            <div
                                className="absolute top-0 bottom-0 w-0.5 bg-content-muted pointer-events-none"
                                style={{ left: `${budgetFraction * 100}%` }}
                                title={populationBinds
                                    ? `Free population caps this ship at ${maxQuantity}`
                                    : `${Math.round(budgetFraction * 100)}% of ${RESOURCE_NAMES.scrap} available for this ship`}
                            />
                        )}
                    </QuantityBar>
                    <Button
                        variant="secondary"
                        size="sm"
                        className="w-8 h-8 !px-0"
                        onClick={() => onChange(1)}
                        {...incrementHold}
                        disabled={!canIncrement}
                        aria-label={`Increase ${def.name} quantity`}
                    >
                        +
                    </Button>
                </div>
                {quantity > 0 && (
                    <p className="text-content-muted text-label mt-1">
                        <ResourceQuantities amounts={{ scrap: thisCost }} includeZero /> total
                    </p>
                )}
            </div>
        </Panel>
    );
}

function RecyclingPageContent({ planetId, race }: { planetId: string; race: PlayerRace }) {
    const { state: { planet, isLoading } } = usePlanetResource();
    const { sciences } = usePlayerStore();
    const { resourceMultiplier, speedMultiplier, sunAges } = useUniverseStore();
    const now = useNow();

    const [selection, setSelection] = useState<Partial<Record<ShipId, number>>>({});
    const [isQueuing, setIsQueuing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const options: PlanetActionOptions = useMemo(
        () => ({ resourceMultiplier, sunAges, speedMultiplier }),
        [resourceMultiplier, sunAges, speedMultiplier]
    );

    const scienceLevels = useMemo(
        () => extractScienceLevels(sciences ?? undefined),
        [sciences]
    );

    const buildingLevels = useMemo(
        () => (planet ? extractBuildingLevels(planet.buildings) : ({} as ReturnType<typeof extractBuildingLevels>)),
        [planet]
    );

    const recycleQueue = useMemo(
        () => (planet?.tickCheckpoints ?? []).filter(
            (e): e is TickCheckpointEventOf<'scrapRecycle'> => e.eventType === 'scrapRecycle' && !e.cancelled
        ),
        [planet]
    );


    // Reset the picker whenever the queue changes underneath it (a batch just
    // queued or completed) so stale quantities don't linger against a fresh scrap balance.
    useEffect(() => {
        setSelection({});
    }, [recycleQueue.length]);

    if (isLoading || !planet || sciences === null) {
        return (
            <div className="min-h-screen bg-space-900 flex items-center justify-center">
                <PageLoadingState message={`Loading ${BUILDING_NAMES.recyclingPlant}…`} />
            </div>
        );
    }

    const plantLevel = buildingLevels.recyclingPlant ?? 0;
    if (plantLevel === 0) {
        return <Navigate to="/zone/industry?highlight=recyclingPlant" replace />;
    }

    const incomePercent = calculateRecyclingIncomePercent(plantLevel);
    const slotsUsed = recycleQueue.length;
    const slotsFree = plantLevel - slotsUsed;

    const dailyWindowExpired = !planet.scrapRecycleDaily || planet.scrapRecycleDaily.resetAt.getTime() <= now;
    const dailyCount = dailyWindowExpired ? 0 : planet.scrapRecycleDaily!.count;
    const dailyRemaining = SCRAP_RECYCLE_DAILY_LIMIT - dailyCount;

    const availableShips = Object.values(SHIP_DEFINITIONS[race]).filter(
        (def) => getUnmetRequirements(def.requirements, buildingLevels, scienceLevels).length === 0
    );

    const dockedFromRecycling = (Object.entries(planet.ships ?? {}) as [string, number | undefined][])
        .filter((entry): entry is [string, number] => !!entry[1] && entry[1] > 0)
        .map(([id, count]) => ({ def: getShipDefinition(race, Number(id) as ShipId), count }));

    const totalScrapCost = recycleScrapRequired(race, selection, plantLevel);
    const totalDurationSeconds = (Object.entries(selection) as [string, number | undefined][])
        .filter((entry): entry is [string, number] => !!entry[1])
        .reduce((sum, [id, qty]) => sum + calculateShipBuildTime(
            race, Number(id) as ShipId, qty, buildingLevels.shipyard ?? 0, buildingLevels.automationCenter ?? 0, speedMultiplier,
        ).totalSeconds, 0);

    const hasSelection = totalScrapCost > 0;
    const scrapAvailable = planet.resources.scrap.current;
    const affordable = totalScrapCost <= scrapAvailable;
    const hasFreeSlot = slotsFree > 0;
    const underDailyCap = dailyRemaining > 0;

    // Free population for a NEW recycle batch, mirroring the server-side
    // check in `planScrapRecycleQueueEntry` (issue #119): capacity minus
    // docked ships, ships in flight, and every pending batch that will dock
    // ships — shipyard batches and the OTHER recycle batches a multi-slot
    // plant can have running alongside this one.
    const populationAvailable = calculateFreePopulation(
        race,
        planet.resources.population.capacity,
        planet.ships,
        planet.tickCheckpoints,
        planet.shipsInFlight
    );
    const totalPopulationNeeded = calculateDockedPopulation(race, selection);
    const hasEnoughPopulation = totalPopulationNeeded <= populationAvailable;

    const disabled = !hasSelection || !affordable || !hasFreeSlot || !underDailyCap || !hasEnoughPopulation || isQueuing;
    // A ceiling that blocks a batch outright is worth naming even with an
    // empty selection — it's what makes every "+" on the page dead, and an
    // unexplained dead control just reads as broken. The per-selection
    // reasons below it only make sense once something IS selected.
    const disabledReason = !hasFreeSlot
        ? 'No free recycling slot — cancel or wait for a batch to finish'
        : populationAvailable <= 0
            ? `Not enough free population — every ${BUILDING_NAMES.hangars} slot is docked, queued or in flight (0 of ${planet.resources.population.capacity} free)`
            : !hasSelection
                ? null
                : !underDailyCap
                    ? `Daily recycle limit reached (${SCRAP_RECYCLE_DAILY_LIMIT}/day)`
                    : !affordable
                        ? `Insufficient ${RESOURCE_NAMES.scrap}`
                        : !hasEnoughPopulation
                            ? `Not enough free population (needs ${totalPopulationNeeded}, ${Math.max(0, populationAvailable)} free)`
                            : null;

    function setQuantity(shipId: ShipId, delta: number) {
        setSelection((prev) => {
            const next = Math.max(0, (prev[shipId] ?? 0) + delta);
            const updated = { ...prev, [shipId]: next };
            if (next === 0) delete updated[shipId];
            return updated;
        });
    }

    function setAbsoluteQuantity(shipId: ShipId, quantity: number) {
        setSelection((prev) => {
            const next = Math.max(0, Math.round(quantity));
            const updated = { ...prev, [shipId]: next };
            if (next === 0) delete updated[shipId];
            return updated;
        });
    }

    async function handleQueue() {
        setIsQueuing(true);
        setError(null);
        try {
            const updated = await startRecycle(planet!.id, selection, options);
            usePlanetStore.setState({ currentPlanet: updated });
            setSelection({});
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Recycle failed');
        } finally {
            setIsQueuing(false);
        }
    }

    return (
        <div className="bg-transparent flex flex-col">
            <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-4 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-heading font-bold text-content-primary">{BUILDING_NAMES.recyclingPlant}</h1>
                    <div className="flex items-center gap-3">
                        <span className="text-content-muted text-body">
                            Slots: {slotsUsed} / {plantLevel} · Income: {incomePercent}% · Today: {dailyCount} / {SCRAP_RECYCLE_DAILY_LIMIT}
                        </span>
                        <Link to="/planet" className={linkButtonClasses('secondary', 'sm')}>← Back to Planet</Link>
                    </div>
                </div>

                {recycleQueue.length > 0 && (
                    <Panel variant="solid" padding="md" data-testid="recycling-queue-panel">
                        <h2 className="text-content-secondary font-semibold mb-1">Recycling Queue</h2>
                        <div className="flex flex-wrap gap-3">
                            {recycleQueue.map((event) => (
                                <QueuedRecycleRow
                                    key={event.eventId}
                                    planetId={planetId}
                                    race={race}
                                    event={event}
                                    now={now}
                                    options={options}
                                />
                            ))}
                        </div>
                    </Panel>
                )}

                {dockedFromRecycling.length > 0 && (
                    <Panel variant="solid" padding="md" data-testid="docked-fleet-panel">
                        <h2 className="text-content-secondary font-semibold mb-1">Docked Fleet</h2>
                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-body text-content-secondary">
                            {dockedFromRecycling.map(({ def, count }) => (
                                <span key={def.id}>
                                    <span className="font-semibold text-content-primary">{def.name}</span>
                                    <span className="text-content-muted"> × {count.toLocaleString()}</span>
                                </span>
                            ))}
                        </div>
                    </Panel>
                )}

                <div className="sticky top-0 z-10 rounded-panel border border-hairline bg-surface-panel backdrop-blur-panel p-4 flex flex-col gap-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <span className="text-content-secondary font-semibold">
                            {RESOURCE_NAMES.scrap} available: <ResourceQuantities amounts={{ scrap: scrapAvailable }} includeZero />
                        </span>
                        {hasSelection && (
                            <span className={`text-body font-semibold ${affordable ? 'text-content-secondary' : 'text-status-danger'}`}>
                                Remaining after selection: <ResourceQuantities amounts={{ scrap: scrapAvailable - totalScrapCost }} includeZero />
                            </span>
                        )}
                    </div>
                    {hasSelection && (
                        <p className="text-content-muted text-body">
                            {summarizeSelection(selection, race)} · <ResourceQuantities amounts={{ scrap: totalScrapCost }} includeZero /> · {formatDuration(totalDurationSeconds)}
                        </p>
                    )}
                    {error && <p className="text-status-danger text-label">{error}</p>}
                    <div className="flex items-center justify-between gap-3">
                        {disabledReason && !isQueuing && (
                            <span className="text-content-muted text-label">{disabledReason}</span>
                        )}
                        <Button
                            variant="primary"
                            size="md"
                            className="ml-auto"
                            onClick={handleQueue}
                            disabled={disabled}
                        >
                            {isQueuing ? 'Queuing…' : 'Queue Recycle'}
                        </Button>
                    </div>
                </div>

                {availableShips.length === 0 ? (
                    <p className="text-content-muted text-body">
                        No ships currently meet the requirements to be built by this {BUILDING_NAMES.recyclingPlant}.
                    </p>
                ) : (
                    <div className="flex flex-col gap-3">
                        {availableShips.map((def) => (
                            <RecycleShipRow
                                key={def.id}
                                def={def}
                                race={race}
                                plantLevel={plantLevel}
                                quantity={selection[def.id] ?? 0}
                                scrapAvailable={scrapAvailable}
                                hasFreeSlot={hasFreeSlot}
                                otherCommittedScrap={recycleScrapRequired(
                                    race,
                                    Object.fromEntries(
                                        (Object.entries(selection) as [string, number | undefined][])
                                            .filter(([id]) => Number(id) !== def.id)
                                    ) as Partial<Record<ShipId, number>>,
                                    plantLevel,
                                )}
                                populationAvailable={populationAvailable}
                                otherCommittedPopulation={calculateDockedPopulation(
                                    race,
                                    Object.fromEntries(
                                        (Object.entries(selection) as [string, number | undefined][])
                                            .filter(([id]) => Number(id) !== def.id)
                                    ) as Partial<Record<ShipId, number>>,
                                )}
                                onChange={(delta) => setQuantity(def.id, delta)}
                                onSetQuantity={(qty) => setAbsoluteQuantity(def.id, qty)}
                            />
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}

export default function RecyclingPage() {
    const { profile } = useAuthStore();
    const activePlanetId = usePlanetStore((s) => s.activePlanetId);

    const planetId = activePlanetId ?? profile?.mainPlanetId ?? '';

    if (!profile || !planetId || !profile.race) {
        return (
            <div className="min-h-screen bg-space-900 flex items-center justify-center">
                <PageLoadingState message="Loading…" />
            </div>
        );
    }

    return (
        <RecyclingPageContent planetId={planetId} race={profile.race} />
    );
}
